import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Screen } from "../components/Screen";
import { ProgressBar } from "../components/ProgressBar";
import { AnswerOptionCard, type AnswerCardState } from "../components/AnswerOptionCard";
import { Button } from "../components/Button";
import { SourceSheet } from "../components/SourceSheet";
import { LoadingScreen } from "../components/LoadingScreen";
import { useQuiz } from "../features/quiz/QuizContext";
import styles from "./QuizScreen.module.css";

const LETTERS = ["A", "B", "C"];

export function QuizScreen() {
  const navigate = useNavigate();
  const { phase, question, total, lastAnswer, submitAnswer, advance, errorMessage } = useQuiz();
  const [sourceOpen, setSourceOpen] = useState(false);

  useEffect(() => {
    setSourceOpen(false);
  }, [question?.id]);

  // Redirect from an effect, not the render body: on a direct/refreshed
  // load of /quiz with no resumable session, "idle" is the very first
  // render's phase — navigating synchronously during render risks leaving
  // the screen permanently blank instead of reliably completing the
  // redirect (see QuizProvider, which now starts at "loading" rather than
  // "idle" whenever a resumable session actually exists, so this only
  // fires for genuinely session-less loads).
  useEffect(() => {
    if (phase === "idle") navigate("/", { replace: true });
  }, [phase, navigate]);

  if (phase === "idle" || (phase === "loading" && !question) || !question) {
    return <LoadingScreen />;
  }

  const showingFeedback = phase === "feedback" && lastAnswer;

  function cardState(optionId: string): AnswerCardState {
    if (!showingFeedback || !lastAnswer) {
      return optionId === lastAnswer?.selectedOptionId ? "selected" : "default";
    }
    if (optionId === lastAnswer.correctOptionId) return "correct";
    if (optionId === lastAnswer.selectedOptionId) return "incorrect";
    return "muted";
  }

  return (
    <Screen fixed>
      <div className={styles.header}>
        <div className={styles.counterRow}>
          <span className={styles.counter}>
            {String(question.index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
        </div>
        <ProgressBar value={question.index + 1} max={total} />
        <span className={styles.topic}>{question.topic}</span>
      </div>

      <div className={styles.body}>
        <h1 className={styles.question}>{question.questionText}</h1>

        <div className={styles.options}>
          {question!.options.map((option, i) => (
            <div key={option.id}>
              <AnswerOptionCard
                letter={LETTERS[i] ?? "?"}
                text={option.text}
                state={cardState(option.id)}
                disabled={phase === "submitting" || !!showingFeedback}
                onClick={() => submitAnswer(option.id)}
              />
              {showingFeedback && cardState(option.id) === "incorrect" && option.feedback && (
                <p className={styles.optionFeedback}>{option.feedback}</p>
              )}
            </div>
          ))}
        </div>

        {errorMessage && phase === "error" && <p className={styles.optionFeedback}>{errorMessage}</p>}

        {showingFeedback && lastAnswer && (
          <div className={styles.feedback}>
            <div className={`${styles.feedbackLabel} ${lastAnswer.correct ? styles.correct : styles.incorrect}`}>
              {lastAnswer.correct ? "Верно" : "Другой ответ"}
            </div>
            <p className={styles.explanation}>{lastAnswer.explanation}</p>
          </div>
        )}
      </div>

      {showingFeedback && lastAnswer && (
        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setSourceOpen(true)}>
            Источник
          </Button>
          <Button
            onClick={async () => {
              await advance();
              if (lastAnswer.isLastQuestion) navigate("/result");
            }}
          >
            {lastAnswer.isLastQuestion ? "Результат" : "Далее"}
          </Button>
        </div>
      )}

      {sourceOpen && lastAnswer && (
        <SourceSheet fragmentId={lastAnswer.sourceFragmentId} onClose={() => setSourceOpen(false)} />
      )}
    </Screen>
  );
}
